"use client";

import { useSyncExternalStore } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { COUNTRY_COOKIE } from "@/lib/markets";
import { WHATSAPP_NUMBER } from "@/lib/business";
import { WhatsAppIcon } from "./whatsapp-button";

const TARGET_PATH = "/republica-dominicana";

function readCookie(name: string): string | null {
  return (
    document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${name}=`))
      ?.split("=")[1] ?? null
  );
}

/** Le cookie ne change pas pendant la visite : rien à écouter. */
const noSubscribe = () => () => {};

/**
 * Bulle WhatsApp flottante, réservée aux visiteurs détectés en République
 * dominicaine — la convention locale pour « contactez-nous ».
 *
 * Elle mène à la page RD, et non directement à WhatsApp : le prospect y voit
 * les tarifs en dollars et le comparatif avant d'écrire, et la page porte déjà
 * deux boutons qui, eux, ouvrent WhatsApp. D'où son absence sur cette page,
 * où elle ferait doublon.
 *
 * Sans elle, la page RD n'était atteignable que par les annonces, un lien de
 * pied de page et les moteurs : un prospect dominicain arrivé sur l'accueil ne
 * voyait ni ses prix, ni WhatsApp.
 *
 * La détection se fait ICI, côté client, en lisant le cookie `rl_country` que
 * le proxy pose depuis la géo-IP. Le PAYS, pas le marché : depuis qu'il n'y a
 * plus que deux marchés (euros / dollars), « International » couvre aussi bien
 * Saint-Domingue que Tokyo. Lire ce cookie côté serveur aurait fait basculer
 * toutes les pages en rendu dynamique — on aurait payé la staticité de tout le
 * site pour une bulle.
 */
export function MarketInvite() {
  const t = useTranslations("marketing.dr");
  const locale = useLocale();
  const pathname = usePathname();
  // `useSyncExternalStore` plutôt qu'un effet : l'instantané SERVEUR vaut
  // toujours `false`, celui du navigateur lit le cookie. React garantit ainsi
  // que le rendu serveur et la première hydratation coïncident, sans passer par
  // un `setState` dans un effet (qui provoque un second rendu en cascade).
  const isDominicanMarket = useSyncExternalStore(
    noSubscribe,
    () => readCookie(COUNTRY_COOKIE) === "DO",
    () => false,
  );

  // Rien à afficher sans numéro renseigné, hors marché dominicain, ou sur la
  // page de destination elle-même.
  if (!WHATSAPP_NUMBER || !isDominicanMarket || pathname.includes(TARGET_PATH)) {
    return null;
  }

  return (
    <Link
      href={`/${locale}${TARGET_PATH}`}
      aria-label={t("inviteLabel")}
      title={t("inviteLabel")}
      className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-[#07301c] shadow-lg transition-transform duration-300 animate-in fade-in zoom-in hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#25D366]"
    >
      <WhatsAppIcon className="h-7 w-7" />
    </Link>
  );
}
